import React, {useState} from "react";
import {colors} from "patient-ping-remedy/packages/theme";
import {
  MenuItem,
  MenuItemIcon,
  MenuItemSpacer,
  MenuItemTrailingIcon,
  MenuItemTypography
} from "patient-ping-remedy/packages/menu";
import {getEnv, useStateConfig} from "@/hooks/useStateConfig";
import PermissionsHelpers from "@/helpers/permissions_helpers";
import permissions_helpers from "@/helpers/permissions_helpers";
import {useAuthStore} from "@/store/auth_store";
import {useOpenBeds} from "@/hooks/useOpenBeds";
import ToolTip from "patient-ping-remedy/packages/tool-tip";
import {ICONS} from "patient-ping-remedy/packages/icon";
import DisplayHelpers from "@/helpers/display_helpers";
import {SSOApplication, SSOLaunchMethod} from "@/api/dto/mixpanel";
import {AppMenu, AppMenuSubMenu, AppMenuSubMenuTitle} from "patient-ping-remedy/packages/application-menu";
import {useRoundTrip} from "@/hooks/useRoundTrip";
import NavigationModal from "@/components/navigation/NavigationModal";
import FindHelpIcon from "@/assets/icons/FindHelp.svg?react";
import RoundTripIcon from "@/assets/icons/Roundtrip.svg?react";
import {useRosterStore} from "@/store/roster_store";
import {useTreatmentConnection} from "@/hooks/useTreatmentConnection";
import {DataPermissionLevel, DataPermissionLevelNew, DataSource} from "@/api/dto/permissions";
import {FeatureToggle} from "@/api/dto/dto";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";

const ApplicationMenu = () => {
  const stateConfig = useStateConfig();
  const { currentUser } = useAuthStore();
  const { openOpenBeds } = useOpenBeds();
  const { openRoundTrip } = useRoundTrip();
  const { openTreatmentConnection } = useTreatmentConnection();
  const treatmentConnectionUrl = stateConfig?.api?.treatmentConnection?.baseUrl;
  const {currentRoster} = useRosterStore();
  const {checkBooleanToggle} = useFeatureToggles();
  const [menuOpened, setMenuOpened] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS) ?? false;

  const crisisEnabled = currentUser ? (isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserDataPermissionNew(
      currentUser,
      DataPermissionLevelNew.SSO,
      DataSource.OB_CRISIS
    ) : PermissionsHelpers.canForUserDataPermission(
      currentUser,
      DataPermissionLevel.SSO,
      DataSource.OB_CRISIS
    )) : false;

  const referralsEnabled = currentUser ? (isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserDataPermissionNew(
      currentUser,
      DataPermissionLevelNew.SSO,
      DataSource.OB_REFERRALS
    ) : PermissionsHelpers.canForUserDataPermission(
      currentUser,
      DataPermissionLevel.SSO,
      DataSource.OB_REFERRALS
    )) : false;

  const isFindHelpReferralAvailable = currentUser && currentRoster && (isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserDataPermissionRosterNew(
      currentUser,
      currentRoster,
      DataPermissionLevelNew.SSO,
      DataSource.FINDHELP,
    ) : PermissionsHelpers.canForUserDataPermissionRoster(
      currentUser,
      currentRoster,
      DataPermissionLevel.SSO,
      DataSource.FINDHELP,
    ));
  const isFindHelpReferralAvailableForState = !!getEnv(stateConfig?.api?.findHelp)?.baseUrl;

  const isRoundTripAvailable = currentUser ?
    (currentUser?.roundtrip_sso) || permissions_helpers.hasAdminRole(currentUser!) : false;

  const isOpenbedsAvailableForState = !!getEnv(stateConfig?.api?.openBeds);


  return (
    <>
      <AppMenu isOpen={menuOpened} toggle={setMenuOpened} width={'32rem'} popoverMarginTop popoverMarginBottom>
        <AppMenuSubMenu onHide={() => setMenuOpened(false)}>
          <AppMenuSubMenuTitle>YOUR APPS</AppMenuSubMenuTitle>
          <MenuItem
            onClick={() =>
              openOpenBeds(SSOApplication.OPENBEDS_REFERRALS, SSOLaunchMethod.APPLICATION_MENU)}
            disabled={!referralsEnabled || !isOpenbedsAvailableForState}
          >
            <MenuItemIcon iconClass={'procedures'} color={colors.primary2} />
            <MenuItemTypography>{stateConfig?.branding.openBeds.referral.service}</MenuItemTypography>
            <MenuItemSpacer />
            {referralsEnabled && isOpenbedsAvailableForState ? (
              <MenuItemTrailingIcon iconClass={ICONS["external-link"]} />
            ) : (
              <ToolTip
                text={currentRoster
                  ? !isOpenbedsAvailableForState
                    ? DisplayHelpers.notAvailableInStateMessage()
                    : DisplayHelpers.contactAdminMessage()
                  : "No group selected."}
                position="bottom-start"
                type="dark"
                testId="referrals-disabled-tooltip"
              >
                <MenuItemTrailingIcon iconClass={ICONS["info-circle"]} />
              </ToolTip>
            )}
          </MenuItem>
          <MenuItem
            onClick={() =>
              openOpenBeds(SSOApplication.OPENBEDS_CRISIS, SSOLaunchMethod.APPLICATION_MENU)}
            disabled={!crisisEnabled || !isOpenbedsAvailableForState}
          >
            <MenuItemIcon iconClass={'ambulance'} color={colors.primary2} />
            <MenuItemTypography>{stateConfig?.branding.openBeds.crisis.service}</MenuItemTypography>
            <MenuItemSpacer />
            {crisisEnabled && isOpenbedsAvailableForState ? (
              <MenuItemTrailingIcon iconClass={ICONS["external-link"]} />
            ) : (
              <ToolTip
                text={currentRoster
                  ? !isOpenbedsAvailableForState
                    ? DisplayHelpers.notAvailableInStateMessage()
                    : DisplayHelpers.contactAdminMessage()
                  : "No group selected."}
                position="bottom-start"
                type="dark"
                testId="crisis-disabled-tooltip"
              >
                <MenuItemTrailingIcon iconClass={ICONS["info-circle"]} />
              </ToolTip>
            )}
          </MenuItem>
          {treatmentConnectionUrl && currentRoster && (
            <MenuItem
              onClick={() => openTreatmentConnection(SSOLaunchMethod.APPLICATION_MENU, treatmentConnectionUrl)}
            >
              <MenuItemIcon iconClass="user-plus" color={colors.primary2} />
              <MenuItemTypography>Treatment connection</MenuItemTypography>
              <MenuItemSpacer />
              <MenuItemTrailingIcon iconClass={ICONS['external-link']} />
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setMenuOpened(false);
              setModalOpened(true);
            }}
            disabled={!isFindHelpReferralAvailable || !isFindHelpReferralAvailableForState}
          >
            <FindHelpIcon/>
            <MenuItemTypography>{stateConfig?.branding.findHelp.service}</MenuItemTypography>
            <MenuItemSpacer />
            {isFindHelpReferralAvailable && isFindHelpReferralAvailableForState ? (
              <MenuItemTrailingIcon iconClass={ICONS["external-link"]} />
            ) : (
              <ToolTip
                text={currentRoster
                  ? !isFindHelpReferralAvailableForState
                    ? DisplayHelpers.notAvailableInStateMessage()
                    : DisplayHelpers.contactAdminMessage()
                  : "No group selected."}
                position="bottom-start"
                type="dark"
                testId="referrals-disabled-tooltip"
              >
                <MenuItemTrailingIcon iconClass={ICONS["info-circle"]} />
              </ToolTip>
            )}
          </MenuItem>
          <MenuItem
            onClick={() => openRoundTrip(SSOLaunchMethod.APPLICATION_MENU)}
            disabled={!isRoundTripAvailable}
          >
            <RoundTripIcon/>
            <MenuItemTypography>{stateConfig?.branding.roundTrip.service}</MenuItemTypography>
            <MenuItemSpacer />
            {isRoundTripAvailable ? (
              <MenuItemTrailingIcon iconClass={ICONS["external-link"]} />
            ) : (
              <ToolTip
                text={DisplayHelpers.contactAdminMessage()}
                position="bottom-start"
                type="dark"
                testId="referrals-disabled-tooltip"
              >
                <MenuItemTrailingIcon iconClass={ICONS["info-circle"]} />
              </ToolTip>
            )}
          </MenuItem>
        </AppMenuSubMenu>
      </AppMenu>
      {modalOpened &&
        <NavigationModal
          hideModal={() => setModalOpened(false)}
        />
      }
    </>
  );
};

export default ApplicationMenu;
